import React, { useContext, useEffect, useState } from 'react';
import { graphql } from 'react-relay/hooks';
import { useLazyLoadQuery } from 'relay-hooks';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Box, Flex, Heading, Text } from '@resideo/blueprint-react';
import { CartContext } from './CartProvider';
import { subscriptionData } from 'data/SubscriptionData';
import HomeTile from 'components/HomeTile';
import PageLoading from 'components/common/PageLoading';
import PageError from 'components/common/PageError';
import SeparatorLine from 'components/common/SeparatorLine';
import { HomeQuery } from './__generated__/HomeQuery.graphql';
import HomeTileSubscribed from 'components/HomeTileSubscribed';
import { resideoProLink } from 'config/externalLinks';

const PageTitle = styled(Heading)`
  height: 50px;
  width: 441px;
  color: ${({ theme }) => theme.colors.header.secondaryLinkActive};
  font-family: 'URW_Din_CondBold';
  font-size: 40px;
  font-weight: bold;
  letter-spacing: 0.91px;
  line-height: 80px;
`;

const ReviewAndAccessText = styled(Text)`
  height: 0.9375rem;
  width: 1030px;
  color: ${({ theme }) => theme.colors.header.secondaryLinkActive};
  font-family: Roboto;
  font-size: 16px;
  letter-spacing: 0;
  line-height: 21.77px;
`;

const SubscribedServicesText = styled(Text)`
  height: 1.875rem;
  width: 325.88px;
  color: ${({ theme }) => theme.colors.header.secondaryLinkActive};
  font-family: Roboto;
  font-size: 21px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 0.3125rem;
  margin-bottom: 0;
`;

const Unsubscribed = styled(Text)`
  color: ${({ theme }) => theme.colors.header.secondaryLinkActive};
  font-family: Roboto;
  font-size: 21px;
  font-weight: bold;
  letter-spacing: 0;
  margin-bottom: 16px;
`;

const Home = () => {
  const { t } = useTranslation();
  const { state, cartActions } = useContext(CartContext);
  const { error, isFetching } = state;
  const [more, showMore] = useState(true);
  const [showMoreIndex, setShowMoreIndex] = useState(-1);

  const { data } = useLazyLoadQuery<HomeQuery>(
    graphql`
      query HomeQuery {
        viewer {
          id
          partnerUsers {
            edges {
              node {
                ... on PartnerUser {
                  partnerAccount {
                    id
                    ... on PartnerAccount {
                      status
                      partnerBrandProfile {
                        ... on PartnerBrandProfile {
                          logo
                          status
                        }
                      }
                      partnerProgramEnrollments {
                        edges {
                          node {
                            id
                            __typename
                            partnerProgram {
                              name
                            }
                            status
                            activationDate
                            billingCycle
                            enrollmentMarket
                            purchaseDate
                            cancellationDate
                            cancellationReason
                            autoRenew
                            ... on HomeownerAppBrandingPartnerProgramEnrollment {
                              logoApproved
                            }
                            subscriptionEndDate
                            billableQuantity
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    {}
  );

  const status =
    data?.viewer?.partnerUsers?.edges[0]?.node?.partnerAccount?.status;
  if (!(status === 'ACTIVE' || status === null)) {
    if (data?.viewer?.partnerUsers?.edges.length === 0) {
      window.location.replace(`${resideoProLink}/company/application`);
    } else if (
      status === 'ARCHIVED' ||
      status === 'PENDING' ||
      status === 'INACTIVE'
    ) {
      // We are not redirecting customer, when status is Active or null
      window.location.replace(`${resideoProLink}/company/application/status`);
    }
  }

  let s = [];
  let u = [];
  useEffect(() => {
    if (data?.viewer) {
      cartActions.setIsFetching(false);
    }
  }, []);

  const dynamicSort = property => (a, b) => {
    return a[property].localeCompare(b[property]);
  };

  if (data?.viewer) {
    const { subscriptions, unsubscribed } = subscriptionData(
      data,
      more,
      showMoreIndex
    );

    s = subscriptions;
    u = unsubscribed;
  }

  return isFetching ? (
    <PageLoading />
  ) : error ? (
    <PageError />
  ) : (
    <Box
      as='main'
      data-test-homepage
      paddingTop='small'
      paddingBottom={['small', 'large']}
      paddingX={['small', 'large']}>
      <PageTitle fontSize={['large', 'xxxLarge']} marginBottom='medium'>
        {t('menu.servicesAndSubscriptions')}
      </PageTitle>
      <ReviewAndAccessText>{t('home.title')}</ReviewAndAccessText>
      <SeparatorLine m='2.0625rem 0' />
      {s.length === 0 && u.length === 0 && (
        <>
          <SubscribedServicesText>Available Services</SubscribedServicesText>
          <Flex>
            <HomeTile
              i={0}
              d={{
                name: `${t('mybusiness.services.branding')} ${t(
                  'mybusiness.services.service'
                )}`,
                status: 'subscribe',
                desc:
                  !more || showMoreIndex !== 0
                    ? t('mybusiness.description.brandingMore')
                    : {
                        text: t('mybusiness.description.brandingLess'),
                        text1: t('mybusiness.description.brandingLess1'),
                        text2: t('mybusiness.description.brandingLess2'),
                      },
                btnLeft: {
                  title: t('mybusiness.servicesAndSubscriptions.viewPlans'),
                  link: `${window.location.href.replace(
                    /\/+$/,
                    ''
                  )}/pdp/home-owner`,
                },
              }}
              more={more}
              showMore={showMore}
              setShowMoreIndex={setShowMoreIndex}
            />
          </Flex>
        </>
      )}

      {s.length > 0 && (
        <>
          <SubscribedServicesText>
            {t('mybusiness.servicesAndSubscriptions.subscribedServices')}
          </SubscribedServicesText>
          <Flex flexWrap='wrap' width={1}>
            {s.sort(dynamicSort('status')).map((d, i) => (
              <HomeTileSubscribed
                data-test-home-tile
                key={i}
                i={i}
                d={d}
                more={more}
                showMore={showMore}
                setShowMoreIndex={setShowMoreIndex}
              />
            ))}
          </Flex>
        </>
      )}
      {u.length > 0 && (
        <>
          <Unsubscribed>
            {t('mybusiness.servicesAndSubscriptions.unsubscribedServices')}
          </Unsubscribed>
          <Flex flexWrap='wrap'>
            {u.map((d, i) => (
              <HomeTile key={i} i={i} d={d} more={more} moreDisabled />
            ))}
          </Flex>
        </>
      )}
    </Box>
  );
};

export default Home;
